/**
 * Unified client notes (tattoo/piercing) parsing and building.
 * Used by backoffice and frontoffice.
 */
import { sanitizeRichTextHtml, htmlToText } from './sanitizeRichTextHtml';
function hasNoteContent(html) {
    if (!html)
        return false;
    return htmlToText(sanitizeRichTextHtml(html)).length > 0;
}
function tryParseNotes(raw) {
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && typeof parsed.kind === 'string') {
            return parsed;
        }
    }
    catch {
        return null;
    }
    return null;
}
/**
 * Get sections from raw clientNotes JSON string.
 * Optional fallbackPiercings and kindHint used by frontoffice for piercing cart fallback.
 */
export function getClientNoteSections(raw, fallbackPiercings, kindHint) {
    const parsed = tryParseNotes(raw);
    if (parsed?.kind === 'piercing' && Array.isArray(parsed.piercings)) {
        return parsed.piercings.flatMap((item) => {
            if (!hasNoteContent(item.noteHtml))
                return [];
            const titleParts = [item.bodyZone, item.piercingType].filter(Boolean);
            return [
                {
                    kind: 'piercing',
                    title: titleParts.join(' - ') || undefined,
                    bodyZone: item.bodyZone,
                    piercingType: item.piercingType,
                    html: sanitizeRichTextHtml(item.noteHtml || ''),
                },
            ];
        });
    }
    if (parsed?.kind === 'tattoo' && hasNoteContent(parsed.noteHtml)) {
        return [
            {
                kind: 'tattoo',
                html: sanitizeRichTextHtml(parsed.noteHtml || ''),
            },
        ];
    }
    if (hasNoteContent(raw)) {
        return [
            {
                kind: kindHint || 'tattoo',
                html: sanitizeRichTextHtml(raw || ''),
            },
        ];
    }
    if (kindHint === 'piercing' && Array.isArray(fallbackPiercings)) {
        return fallbackPiercings.map((p) => {
            const titleParts = [p.bodyZone, p.piercingType].filter(Boolean);
            return {
                kind: 'piercing',
                title: titleParts.join(' - ') || undefined,
                bodyZone: p.bodyZone,
                piercingType: p.piercingType,
                html: '',
            };
        });
    }
    return [];
}
/** Plain-text summary of client notes (backoffice calendar, etc.). */
export function clientNotesToPlainText(raw) {
    const sections = getClientNoteSections(raw);
    const parts = sections
        .map((section) => {
        const text = htmlToText(section.html);
        if (!text)
            return '';
        return section.title ? `${section.title}: ${text}` : text;
    })
        .filter(Boolean);
    return parts.join(' | ');
}
export function buildClientNotesPayload(kind, sections) {
    if (kind === 'tattoo') {
        const html = sections[0]?.html || '';
        return { kind: 'tattoo', noteHtml: html };
    }
    return {
        kind: 'piercing',
        piercings: sections.map((section) => ({
            bodyZone: section.bodyZone,
            piercingType: section.piercingType,
            noteHtml: section.html,
        })),
    };
}
